import { healthContract } from "./health.contract";
import {
  testCustomerMatchContract,
  testForwardOriginalGraphMessageContract,
  testListRecentMailboxMessagesContract,
  testUpnWriteAccessContract,
} from "./test.contract";
import { memoryBankContracts } from "./memory-bank.contract";
import { orgConfigModulesContract } from "./org-config/modules/module-configs.contract";
import { emailIngestConfigsContracts } from "./org-config/email-ingest-configs.contract";
import { sandboxContracts } from "./org-config/sandbox.contract";
import { sandboxesContracts } from "./sandboxes.contract";
import { accountsReceivableContracts } from "./accounts-receivable";
import { communicationsContracts } from "./communications";
import { integrationsContracts } from "./integrations";
import { workflowContracts } from "./workflows.contract";
import { featureFlagsContracts } from "./testing";
import { accountsPayableContracts } from "./accounts-payable";
import { entityAssociationsContracts } from "./core/entity-associations.contract";
import { activityLogContracts } from "./core/activity-log.contract";
import { usersContracts } from "./core/users.contract";
import { companiesContracts } from "./core/companies.contract";
import { documentContracts } from "./core/documents.contract";
import { externalSystemReferencesContracts } from "./core/external-system-references.contract";
import { entityModuleConfigContracts } from "./core/entity-module-config.contract";
import { entityQuickFindContracts } from "./core/entity-quick-find.contract";
import { authzContracts } from "./authz.contract";
import { customViewsContracts } from "./core/custom-views.contract";
import { userTableColumnConfigsContracts } from "./core/user-table-column-configs.contract";
import { orgTableColumnConfigsContracts } from "./core/org-table-column-configs.contract";
import { orgUiSectionConfigsContracts } from "./core/org-ui-section-configs.contract";
import { userPreferencesContracts } from "./core/user-preferences.contract";
import { emailSignatureContracts } from "./core/email-signature.contract";
import {
  transactionsContracts,
  paymentsContracts,
  chargesContracts,
} from "./finance";
import { rebatesContracts } from "./rebates";
import { supplyChainContracts } from "./supply-chain";
import { inventoryManagementContracts } from "./inventory-management";
import { procurementContracts } from "./procurement";
import { procurementDemoContracts } from "./procurement-demo.contract";
import { shipAndDebitContracts } from "./ship-and-debit";
import { apiKeysContracts } from "./api-keys.contract";
import { workflowApiTokensContracts } from "./workflow-api-tokens.contract";
import { outOfOfficeContracts } from "./out-of-office.contract";
import { otpContracts } from "./otp.contract";
import { agentContracts } from "./agent.contract";
import { tasksContracts } from "./tasks.contract";
import { commentsContracts } from "./comments.contract";
import { agentDefinitionContracts } from "./agent-definitions.contract";
import { suggestedActionContracts } from "./suggested-actions.contract";
import { emailDraftsContracts } from "./email-drafts.contract";
import { composeReplyContracts } from "./compose-reply.contract";
import { credentialsContracts } from "./credentials.contract";
import { leaseableResourceContracts } from "./leaseable-resources.contract";
import { externalApisContracts } from "./external-apis.contract";
import { organizationGroupsContracts } from "./organization/groups.contract";
import { organizationAddressContracts } from "./organization/address.contract";
import { organizationMembersContracts } from "./organization/members.contract";
import { ediContracts } from "./edi.contract";
import { trafficGeneratorContracts } from "./admin/traffic-generator.contract";
import { auditReportsContracts } from "./audit-reports.contract";
import { backfillsContracts } from "./admin/backfills.contract";
import { authContracts } from "./auth.contract";
import { dataAssetContracts } from "./data-assets.contract";
import { statefulTablesContracts } from "./stateful-tables.contract";
import { testSuiteContracts } from "./test-suites.contract";
import { annotationContracts } from "./annotation.contract";
import { schemaDefinitionsContracts } from "./core/schema-definitions.contract";
import { schemaEvalsContracts } from "./core/schema-evals.contract";
import { userFeedbackContracts } from "./core/user-feedback.contract";
import { uiBugReportContracts } from "./core/ui-bug-report.contract";
import { dataSourcesContracts } from "./core/data-sources.contract";
import { qaContracts } from "./qa.contract";
import { guardrailPoliciesContracts } from "./guardrail-policies.contract";
import { resourcePrivilegesContracts } from "./resource-privileges.contract";
import { tenantContracts } from "./tenant/tenant.contract";
import { tagDefinitionsContracts } from "./tag-definitions.contract";
import { resourceTagsContracts } from "./resource-tags.contract";
import { chatAgentContracts, streamingAudioContracts } from "./ai";
import { controlPlaneOrgContracts } from "./org-config/control-plane.contract";
import { fileExtractionContracts } from "./tools/file-extraction.contract";

/**
 * Main contract router that defines all available RPC procedures
 */
export const contractRouter = {
  // Auth endpoints
  auth: authContracts,
  // Health check endpoints
  health: {
    check: healthContract,
  },
  // Test endpoints
  test: {
    customerMatch: testCustomerMatchContract,
    upnWriteAccess: testUpnWriteAccessContract,
    forwardOriginalGraphMessage: testForwardOriginalGraphMessageContract,
    listRecentMailboxMessages: testListRecentMailboxMessagesContract,
  },
  // Testing utilities
  testing: {
    featureFlags: featureFlagsContracts,
  },
  // Authorization endpoints
  authz: authzContracts,
  // Memory bank note endpoints
  memoryBank: memoryBankContracts,
  // Organization configuration endpoints
  org: {
    controlPlane: controlPlaneOrgContracts,
    config: {
      modules: orgConfigModulesContract,
      emailIngest: emailIngestConfigsContracts,
      sandbox: sandboxContracts,
    },
  },
  // Core system endpoints
  core: {
    entityAssociations: entityAssociationsContracts,
    customViews: customViewsContracts,
    activityLog: activityLogContracts,
    users: usersContracts,
    companies: companiesContracts,
    documents: documentContracts,
    userTableColumnConfigs: userTableColumnConfigsContracts,
    orgTableColumnConfigs: orgTableColumnConfigsContracts,
    orgUiSectionConfigs: orgUiSectionConfigsContracts,
    userPreferences: userPreferencesContracts,
    emailSignature: emailSignatureContracts,
    externalSystemReferences: externalSystemReferencesContracts,
    entityModuleConfig: entityModuleConfigContracts,
    entityQuickFind: entityQuickFindContracts,
    schemaDefinitions: schemaDefinitionsContracts,
    schemaEvals: schemaEvalsContracts,
    userFeedback: userFeedbackContracts,
    uiBugReport: uiBugReportContracts,
    dataSources: dataSourcesContracts,
  },
  // Integrations / plugins endpoints
  integrations: integrationsContracts,
  // External API registry (org-scoped, used by `external_http_call` step)
  externalApis: externalApisContracts,
  // Accounts receivable endpoints
  accountsReceivable: accountsReceivableContracts,
  // Workflow endpoints
  workflows: workflowContracts,
  // Sandbox instance lifecycle + observability (the vending primitive)
  sandboxes: sandboxesContracts,
  // Guardrail policy management endpoints
  guardrailPolicies: guardrailPoliciesContracts,
  // Resource privilege admin endpoints (tenant-level data scoping)
  resourcePrivileges: resourcePrivilegesContracts,
  // Tenant management endpoints (multi-org admin)
  tenant: tenantContracts,
  // ABAC tag definition admin endpoints (tenant-level)
  tagDefinitions: tagDefinitionsContracts,
  // Resource tag admin endpoints — tag rows against resources
  resourceTags: resourceTagsContracts,
  // Data asset library endpoints
  dataAssets: dataAssetContracts,
  // Stateful tables: versioned schema-applied tables ingested from workflows
  statefulTables: statefulTablesContracts,
  // Test suite endpoints (workflow step evals)
  testSuites: testSuiteContracts,
  // Annotation editor endpoints (document provenance for correcting outputs)
  annotation: annotationContracts,
  // Communications endpoints
  communications: communicationsContracts,
  // Accounts payable endpoints
  accountsPayable: accountsPayableContracts,
  // Finance endpoints
  finance: {
    payments: paymentsContracts,
    transactions: transactionsContracts,
    charges: chargesContracts,
  },
  // Rebates endpoints
  rebates: rebatesContracts,
  // Inventory management endpoints
  inventoryManagement: inventoryManagementContracts,
  procurement: procurementContracts,
  // THROWAWAY DEMO SUPPORT: Bausch + Lomb procurement demo document Q&A
  procurementDemo: procurementDemoContracts,
  // Supply chain endpoints
  supplyChain: supplyChainContracts,
  // Ship and Debit endpoints
  shipAndDebit: shipAndDebitContracts,
  // API Keys endpoints
  apiKeys: apiKeysContracts,
  // Workflow API token endpoints (lineage-bound machine credentials)
  workflowApiTokens: workflowApiTokensContracts,
  // Out-of-office period endpoints
  outOfOffice: outOfOfficeContracts,
  // OTP management endpoints
  otp: otpContracts,
  // Agent management endpoints
  agent: agentContracts,
  // Task management endpoints
  tasks: tasksContracts,
  // Comment endpoints (polymorphic, currently used by tasks)
  comments: commentsContracts,
  // Agent definition CRUD endpoints
  agentDefinitions: agentDefinitionContracts,
  // Suggested action endpoints (AI agent proposals)
  suggestedActions: suggestedActionContracts,
  // Email draft endpoints (AI-generated drafts)
  emailDrafts: emailDraftsContracts,
  composeReply: composeReplyContracts,
  // Credential management endpoints
  credentials: credentialsContracts,
  leaseableResources: leaseableResourceContracts,
  // Organization group management endpoints
  organization: {
    groups: organizationGroupsContracts,
    address: organizationAddressContracts,
    members: organizationMembersContracts,
  },
  // EDI outbound management endpoints
  edi: ediContracts,
  // QA testing endpoints
  qa: qaContracts,
  // Admin tooling endpoints
  admin: {
    trafficGenerator: trafficGeneratorContracts,
    backfills: backfillsContracts,
  },
  // Audit report generation endpoints
  auditReports: auditReportsContracts,
  // AI endpoints
  ai: {
    chatAgent: chatAgentContracts,
    streamingAudio: streamingAudioContracts,
  },
  // Beta tools (disposable namespace)
  tools: {
    fileExtraction: fileExtractionContracts,
  },
} as const;

// Re-export types from audit report contracts
export * from "./audit-reports.contract";
// Re-export types from resource privileges contract
export * from "./resource-privileges.contract";
// Re-export types from tenant contracts
export * from "./tenant/tenant.contract";
// Re-export types from AI contracts
export * from "./ai";
// Re-export types from auth contracts
export * from "./auth.contract";
// Re-export types from authz contracts
export * from "./authz.contract";
// Re-export types from memory bank contracts
export * from "./memory-bank.contract";
// Re-export types from accounts receivable contracts
export * from "./accounts-receivable/sales-orders.contract";
export * from "./accounts-receivable/sales-order-drafts.contract";
export * from "./accounts-receivable/draft-sales-orders.contract";
export * from "./accounts-receivable/sales-orders-v3.contract";
export * from "./accounts-receivable/customer-products.contract";
export * from "./accounts-receivable/discounts.contract";
export * from "./accounts-receivable/order-approvals.contract";
export * from "./accounts-receivable/approval-config.contract";
export * from "./accounts-receivable/sales-orders-rpc.contract";
export * from "./accounts-receivable/ar-sync.contract";
export * from "./accounts-receivable/ar-invoices.contract";
export * from "./accounts-receivable/ar-payments.contract";
export * from "./accounts-receivable/ar-payment-applications.contract";
export * from "./accounts-receivable/ar-aging.contract";
export * from "./accounts-receivable/ar-summary.contract";
export * from "./accounts-receivable/order-stage-report.contract";
export * from "./accounts-receivable/ar-custom-aging.contract";
export * from "./accounts-receivable/ar-saved-reports.contract";
export * from "./accounts-receivable/ar-customer-profile.contract";
export * from "./accounts-receivable/ar-customer-intelligence.contract";
export * from "./accounts-receivable/ar-cash-receipts.contract";
export * from "./accounts-receivable/ar-customer-contacts.contract";
export * from "./accounts-receivable/ar-customer-comms.contract";
export * from "./accounts-receivable/ar-statement-email.contract";
export * from "./accounts-receivable/ar-statement-defaults.contract";
export * from "./core/entity-quick-find.contract";
export * from "./accounts-receivable/ar-remittances.contract";
export * from "./accounts-receivable/price-lists.contract";
export * from "./accounts-receivable/ar-collection-threads.contract";
export * from "./accounts-receivable/ar-collection-email-drafts.contract";
export * from "./accounts-receivable/ar-short-pay-recommendations.contract";
export * from "./accounts-receivable/ar-over-payment-recommendations.contract";
export * from "./accounts-receivable/ar-customer-comms-notes.contract";
// Re-export types from communications contracts
export * from "./communications";
// Re-export types from integrations contracts
export * from "./integrations";
// Re-export types from workflows contracts
export * from "./workflows.contract";
// Re-export types from testing contracts
export * from "./testing";
// Re-export types from accounts payable contracts
export * from "./accounts-payable";
// Re-export types from finance contracts
export * from "./finance";
// Re-export types from core contracts
export * from "./core/entity-associations.contract";
export * from "./core/custom-views.contract";
export * from "./core/activity-log.contract";
export * from "./core/users.contract";
export * from "./core/companies.contract";
export * from "./core/documents.contract";
export * from "./core/user-table-column-configs.contract";
export * from "./core/org-table-column-configs.contract";
export * from "./core/org-ui-section-configs.contract";
export * from "./core/user-preferences.contract";
export * from "./core/email-signature.contract";
export * from "./core/external-system-references.contract";
export * from "./core/entity-module-config.contract";
export * from "./core/schema-definitions.contract";
export * from "./core/schema-evals.contract";
export * from "./core/user-feedback.contract";
export * from "./core/ui-bug-report.contract";
// Re-export types from rebates contracts
export * from "./rebates";
// Re-export types from inventory management contracts
export * from "./inventory-management";
export * from "./procurement";
export * from "./procurement-demo.contract";
// Re-export types from supply chain contracts
export * from "./supply-chain";
// Re-export types from ship and debit contracts
export * from "./ship-and-debit";
// Re-export types from api keys contracts
export * from "./api-keys.contract";
// Re-export types from workflow api token contracts
export * from "./workflow-api-tokens.contract";
// Re-export types from out-of-office contracts
export * from "./out-of-office.contract";
// Re-export types from OTP contracts
export * from "./otp.contract";
// Re-export types from agent contracts
export * from "./agent.contract";
// Re-export types from tasks contracts
export * from "./tasks.contract";
// Re-export types from comments contracts
export * from "./comments.contract";
// Re-export types from agent definition contracts
export * from "./agent-definitions.contract";
// Re-export types from suggested action contracts
export * from "./suggested-actions.contract";
// Re-export types from email draft contracts
export * from "./email-drafts.contract";
export * from "./compose-reply.contract";
// Re-export types from credentials contracts
export * from "./credentials.contract";
// Re-export types from external API registry contracts
export * from "./external-apis.contract";
// Re-export types from organization contracts
export * from "./organization/groups.contract";
export * from "./organization/address.contract";
export * from "./organization/members.contract";
// Re-export types from EDI contracts
export * from "./edi.contract";
// Re-export types from email ingest config contracts
export * from "./org-config/email-ingest-configs.contract";
export * from "./org-config/sandbox.contract";
// Re-export sandbox instance lifecycle contract + shared trace schemas
export * from "./sandboxes.contract";
// Re-export types from data asset contracts
export * from "./data-assets.contract";
export * from "./stateful-tables.contract";
// Re-export types from QA contracts
export * from "./qa.contract";
// Re-export types from test suite contracts
export * from "./test-suites.contract";
export * from "./annotation.contract";
// Re-export types from guardrail policy contracts
export * from "./guardrail-policies.contract";
export * from "./core/data-sources.contract";
// Re-export types from control plane org contracts
export * from "./org-config/control-plane.contract";
// Re-export types from ABAC tag-definitions and resource-tags contracts
export * from "./tag-definitions.contract";
export * from "./resource-tags.contract";
export * from "./tools/file-extraction.contract";
